import { useIsOnline } from '@/hooks/useIsOnline';
import { buildOrgUrl, getOrgSlugFromHostname } from '@/core/domain';
import { execute } from '@/graphql/execute';
import { offlineCatalogAtom } from '@/lib/offline/catalog';
import { filterProjectsByName, offlineReadyProjects } from '@/lib/offline/offlineProjects';
import { searchProjectsQuery } from '@/queries/search.query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { FolderOpen, Loader2 } from 'lucide-react';
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';

interface GlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Minimal shape of a cached project (from the persisted `projects` query). */
interface CachedProject {
  id: string;
  name: string;
  organizationId: string;
}

const GlobalSearch: FunctionComponent<GlobalSearchProps> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const isOnline = useIsOnline();
  const catalog = useAtomValue(offlineCatalogAtom);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (!search.trim() || search.trim().length < 2) {
      setDebouncedSearch('');
      return;
    }
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 200);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // ── Online: network search across all orgs ────────────────────────────────
  const { data, isLoading, isError } = useQuery({
    queryKey: ['searchProjects', debouncedSearch],
    queryFn: () => execute(searchProjectsQuery, { query: debouncedSearch }),
    enabled: open && isOnline && debouncedSearch.length >= 2,
  });

  const currentSlug = getOrgSlugFromHostname();
  const results = data?.searchProjects ?? [];

  const grouped = useMemo(() => {
    const currentOrg: typeof results = [];
    const otherOrgs = new Map<string, { name: string; items: typeof results }>();

    for (const result of results) {
      if (result.organizationSlug === currentSlug) {
        currentOrg.push(result);
      } else {
        const key = result.organizationSlug;
        if (!otherOrgs.has(key)) {
          otherOrgs.set(key, { name: result.organizationName, items: [] });
        }
        otherOrgs.get(key)!.items.push(result);
      }
    }

    return { currentOrg, otherOrgs };
  }, [results, currentSlug]);

  // ── Offline: filter the cached, downloaded-and-ready projects locally ─────
  // The `projects` query is persisted (Phase 1.5), so it's available offline.
  // We only surface projects whose offline download is `ready` — the ones the
  // user can actually open with no connection.
  const offlineResults = useMemo<CachedProject[]>(() => {
    if (isOnline || !open) return [];
    const cached = queryClient.getQueriesData<{ projects?: CachedProject[] }>({ queryKey: ['projects'] });
    const seen = new Set<string>();
    const unique: CachedProject[] = [];
    for (const [, value] of cached) {
      for (const p of value?.projects ?? []) {
        if (!seen.has(p.id)) {
          seen.add(p.id);
          unique.push(p);
        }
      }
    }
    return filterProjectsByName(offlineReadyProjects(unique, catalog), search);
  }, [isOnline, open, queryClient, catalog, search]);

  const handleSelect = useCallback(
    (id: string, slug?: string) => {
      onOpenChange(false);
      if (!slug || slug === currentSlug) {
        navigate(`/projects/${id}`);
      } else {
        window.open(buildOrgUrl(slug, `/projects/${id}`));
      }
    },
    [currentSlug, navigate, onOpenChange],
  );

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title='Search Projects'
      description='Search across your projects'
      showCloseButton={false}>
      <Command shouldFilter={false}>
        <CommandInput
          placeholder={isOnline ? 'Search projects...' : 'Search downloaded projects...'}
          value={search}
          onValueChange={setSearch}
        />
        <CommandList>
          {!isOnline ? (
            // Offline: local filter over downloaded projects (instant, no debounce).
            <>
              {offlineResults.length === 0 && (
                <CommandEmpty>
                  {search.trim() ? 'No downloaded projects match.' : 'No projects available offline.'}
                </CommandEmpty>
              )}
              {offlineResults.length > 0 && (
                <CommandGroup heading='Available offline'>
                  {offlineResults.map((project) => (
                    <CommandItem
                      key={project.id}
                      value={project.id}
                      className='py-1.5'
                      onSelect={() => handleSelect(project.id)}>
                      <FolderOpen className='size-4 shrink-0' />
                      <span className='truncate'>{project.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </>
          ) : (
            <>
              {debouncedSearch.length < 2 && <CommandEmpty>Start typing to search projects...</CommandEmpty>}

              {debouncedSearch.length >= 2 && !isLoading && (results.length === 0 || isError) && (
                <CommandEmpty>No projects found.</CommandEmpty>
              )}

              {isLoading && (
                <div className='flex items-center justify-center py-6'>
                  <Loader2 className='size-4 animate-spin' />
                </div>
              )}

              {grouped.currentOrg.length > 0 && (
                <CommandGroup heading='Current Organization'>
                  {grouped.currentOrg.map((result) => (
                    <CommandItem
                      key={result.id}
                      value={result.id}
                      className='py-1.5'
                      onSelect={() => handleSelect(result.id, result.organizationSlug)}>
                      <FolderOpen className='size-4 shrink-0' />
                      <div className='flex flex-col gap-0 min-w-0'>
                        <span className='truncate'>{result.name}</span>
                        {result.description && (
                          <span className='text-muted-foreground text-xs truncate'>{result.description}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {[...grouped.otherOrgs.entries()].map(([slug, { name, items }]) => (
                <CommandGroup key={slug} heading={name}>
                  {items.map((result) => (
                    <CommandItem
                      key={result.id}
                      value={result.id}
                      className='py-1.5'
                      onSelect={() => handleSelect(result.id, result.organizationSlug)}>
                      <FolderOpen className='size-4 shrink-0' />
                      <div className='flex flex-col gap-0 min-w-0'>
                        <span className='truncate'>{result.name}</span>
                        {result.description && (
                          <span className='text-muted-foreground text-xs truncate'>{result.description}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </>
          )}
        </CommandList>
      </Command>
    </CommandDialog>
  );
};

export default GlobalSearch;
